import React, { useState } from "react";
import {
  Bell,
  X,
  Check,
  AlertCircle,
  Info,
  Upload,
  Eye,
  Package,
  CreditCard,
  FileText,
  Clock,
  CheckCircle,
  XCircle,
  Loader2,
} from "lucide-react";
import { useQuery, useMutation } from "@apollo/client/react";
import { GET_NOTIFICATIONS } from "../../graphql/queries/notification";
import {
  MARK_NOTIFICATION_AS_READ,
  MARK_ALL_NOTIFICATIONS_AS_READ,
  DELETE_NOTIFICATION,
} from "../../graphql/mutations/notification";
import { useAuth } from "../../hooks/useAuth";
import type {
  Notification,
  NotificationType,
  NotificationCategory,
} from "@custodio-ai/types";

interface NotificationCenterProps {
  isOpen: boolean;
  onClose: () => void;
}

export function NotificationCenter({
  isOpen,
  onClose,
}: NotificationCenterProps) {
  const { user } = useAuth();
  const [markingAsRead, setMarkingAsRead] = useState<string[]>([]);

  const { data, loading, refetch } = useQuery(GET_NOTIFICATIONS, {
    variables: { userId: user?.id || "" },
    skip: !user?.id || !isOpen,
  });

  const [markAsReadMutation] = useMutation(MARK_NOTIFICATION_AS_READ, {
    refetchQueries: [
      { query: GET_NOTIFICATIONS, variables: { userId: user?.id } },
    ],
  });

  const [markAllAsReadMutation] = useMutation(MARK_ALL_NOTIFICATIONS_AS_READ, {
    refetchQueries: [
      { query: GET_NOTIFICATIONS, variables: { userId: user?.id } },
    ],
  });

  const [deleteNotificationMutation] = useMutation(DELETE_NOTIFICATION, {
    refetchQueries: [
      { query: GET_NOTIFICATIONS, variables: { userId: user?.id } },
    ],
  });

  const notifications: Notification[] = (data?.notifications || []).map(
    (n: any) => ({
      id: n.id,
      user_id: n.userId,
      type: n.type,
      category: n.category,
      title: n.title,
      message: n.message,
      data: n.data,
      read: n.read,
      created_at: n.createdAt,
    }),
  );

  const getNotificationIcon = (
    type: NotificationType,
    category: NotificationCategory,
  ) => {
    switch (category) {
      case "upload":
        return <Upload className="h-5 w-5" />;
      case "ocr":
        return <Eye className="h-5 w-5" />;
      case "billing":
        return <Package className="h-5 w-5" />;
      case "payment":
        return <CreditCard className="h-5 w-5" />;
      case "system":
        return <Bell className="h-5 w-5" />;
      default:
        switch (type) {
          case "success":
            return <CheckCircle className="h-5 w-5" />;
          case "error":
            return <XCircle className="h-5 w-5" />;
          case "warning":
            return <AlertCircle className="h-5 w-5" />;
          default:
            return <Info className="h-5 w-5" />;
        }
    }
  };

  const getNotificationColor = (type: NotificationType) => {
    switch (type) {
      case "success":
        return "text-green-600 bg-green-100";
      case "error":
        return "text-red-600 bg-red-100";
      case "warning":
        return "text-yellow-600 bg-yellow-100";
      default:
        return "text-blue-600 bg-blue-100";
    }
  };

  const formatTimeAgo = (dateString: string) => {
    const now = new Date();
    const date = new Date(dateString);
    const diffInSeconds = Math.floor((now.getTime() - date.getTime()) / 1000);

    if (diffInSeconds < 60) {
      return "Just now";
    } else if (diffInSeconds < 3600) {
      const minutes = Math.floor(diffInSeconds / 60);
      return `${minutes}m ago`;
    } else if (diffInSeconds < 86400) {
      const hours = Math.floor(diffInSeconds / 3600);
      return `${hours}h ago`;
    } else {
      const days = Math.floor(diffInSeconds / 86400);
      return `${days}d ago`;
    }
  };

  const markAsRead = async (notificationId: string) => {
    setMarkingAsRead((prev) => [...prev, notificationId]);

    try {
      await markAsReadMutation({ variables: { id: notificationId } });
      await refetch();
    } catch (error) {
      console.error("Error marking notification as read:", error);
    } finally {
      setMarkingAsRead((prev) => prev.filter((id) => id !== notificationId));
    }
  };

  const markAllAsRead = async () => {
    if (!user?.id) return;

    try {
      await markAllAsReadMutation({ variables: { userId: user.id } });
      await refetch();
    } catch (error) {
      console.error("Error marking all notifications as read:", error);
    }
  };

  const deleteNotification = async (notificationId: string) => {
    try {
      await deleteNotificationMutation({ variables: { id: notificationId } });
      await refetch();
    } catch (error) {
      console.error("Error deleting notification:", error);
    }
  };

  if (!isOpen) return null;

  const unreadCount = notifications.filter((n) => !n.read).length;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-start justify-end z-50 p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md max-h-[90vh] overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-secondary-200">
          <div className="flex items-center space-x-3">
            <div className="p-2 bg-primary-100 rounded-lg">
              <Bell className="h-6 w-6 text-primary-600" />
            </div>
            <div>
              <h2 className="text-xl font-bold text-secondary-900">
                Notifications
              </h2>
              {unreadCount > 0 && (
                <p className="text-sm text-secondary-600">
                  {unreadCount} unread
                </p>
              )}
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-secondary-100 rounded-full transition-colors"
          >
            <X className="h-6 w-6 text-secondary-400" />
          </button>
        </div>

        {/* Actions */}
        {unreadCount > 0 && (
          <div className="p-4 border-b border-secondary-200 bg-secondary-50">
            <button
              onClick={markAllAsRead}
              className="text-sm text-primary-600 hover:text-primary-700 font-medium"
            >
              Mark all as read
            </button>
          </div>
        )}

        {/* Notifications List */}
        <div className="max-h-[calc(90vh-200px)] overflow-y-auto">
          {loading ? (
            <div className="flex justify-center items-center h-32">
              <Loader2 className="h-6 w-6 animate-spin text-primary-600" />
            </div>
          ) : notifications.length === 0 ? (
            <div className="text-center py-12">
              <div className="mx-auto w-16 h-16 bg-secondary-100 rounded-full flex items-center justify-center mb-4">
                <Bell className="h-8 w-8 text-secondary-400" />
              </div>
              <h3 className="text-lg font-semibold text-secondary-900 mb-2">
                No notifications yet
              </h3>
              <p className="text-secondary-600">
                You'll see updates about your uploads and processing here
              </p>
            </div>
          ) : (
            <div className="divide-y divide-secondary-200">
              {notifications.map((notification) => (
                <div
                  key={notification.id}
                  className={`p-4 hover:bg-secondary-50 transition-colors ${
                    !notification.read ? "bg-blue-50/50" : ""
                  }`}
                >
                  <div className="flex items-start space-x-3">
                    <div
                      className={`p-2 rounded-lg ${getNotificationColor(
                        notification.type,
                      )}`}
                    >
                      {getNotificationIcon(
                        notification.type,
                        notification.category,
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <h4 className="text-sm font-semibold text-secondary-900 mb-1">
                            {notification.title}
                          </h4>
                          <p className="text-sm text-secondary-600 mb-2">
                            {notification.message}
                          </p>
                          <p className="text-xs text-secondary-500">
                            {formatTimeAgo(notification.created_at)}
                          </p>
                        </div>
                        <div className="flex items-center space-x-1 ml-2">
                          {!notification.read && (
                            <button
                              onClick={() => markAsRead(notification.id)}
                              disabled={markingAsRead.includes(notification.id)}
                              className="p-1 text-secondary-400 hover:text-green-600 transition-colors"
                              title="Mark as read"
                            >
                              {markingAsRead.includes(notification.id) ? (
                                <Loader2 className="h-4 w-4 animate-spin" />
                              ) : (
                                <Check className="h-4 w-4" />
                              )}
                            </button>
                          )}
                          <button
                            onClick={() => deleteNotification(notification.id)}
                            className="p-1 text-secondary-400 hover:text-red-600 transition-colors"
                            title="Delete"
                          >
                            <X className="h-4 w-4" />
                          </button>
                        </div>
                      </div>
                      {!notification.read && (
                        <div className="absolute left-2 top-1/2 transform -translate-y-1/2 w-2 h-2 bg-primary-600 rounded-full"></div>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
